import { showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { OrganizationsSelection } from '@juvare/contact-management/ui/features/organizations';
import { usePermission } from '@juvare/contact-management/ui/hooks';
import { Box, Button, Divider, FormControl, MenuItem, Radio, RadioGroup, Select, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useAlertConfig, useAlertUiTranslation, useUserState } from '../../../../../hooks';
import { mapToAlertWizardFormData } from '../../AlertDialog/Utils';
export function shareTemplateDialog(params) {
    showCustomDialog({
        content: (close, ariaLabelledBy) => React.createElement(Content, Object.assign({}, params, { onClose: close, ariaLabelledBy: ariaLabelledBy })),
    });
}
const Content = (props) => {
    const { data, onSubmit, onClose } = props;
    const { permissions } = data;
    const userState = useUserState();
    const alertConfig = useAlertConfig();
    const { t } = useAlertUiTranslation();
    const [selectedOrgIds, setSelectedOrgIds] = React.useState([]);
    const [selectedOrgRights, setSelectedOrgRights] = React.useState({});
    const { requireAnySystemPermission } = usePermission();
    const isUserTemplateOwnerOrAdmin = React.useMemo(() => {
        return requireAnySystemPermission('manage-notifications') || userState.id === (data === null || data === void 0 ? void 0 : data.createdById);
    }, [requireAnySystemPermission, userState, data]);
    const [activePermissionType, setActivePermissionType] = React.useState('');
    React.useEffect(() => {
        if (permissions && permissions.length > 0) {
            if (permissions.length === 1 && permissions[0].type === 'user') {
                setActivePermissionType('private');
            }
            else if (permissions.some((permission) => permission.type === 'anyone')) {
                setActivePermissionType('anyone');
            }
            else {
                setActivePermissionType('organization');
                const organizationIds = [];
                const organizationRights = {};
                permissions
                    .filter((permission) => { var _a; return ((_a = permission.externalId) === null || _a === void 0 ? void 0 : _a.startsWith('org_')) && permission.type === 'owner'; })
                    .forEach((permission) => {
                    var _a;
                    const organizationId = (_a = permission.externalId) === null || _a === void 0 ? void 0 : _a.replace('org_', '');
                    if (organizationId) {
                        organizationIds.push(organizationId);
                        organizationRights[organizationId] = permission.right;
                    }
                });
                setSelectedOrgIds(organizationIds);
                setSelectedOrgRights(organizationRights);
            }
        }
    }, [permissions]);
    const handleOrganizationRightChange = (orgId, right) => {
        const updatedOrgRights = Object.assign({}, selectedOrgRights);
        updatedOrgRights[orgId] = right;
        setSelectedOrgRights(updatedOrgRights);
    };
    const handleOrganizationsChange = (organizationIds) => {
        if (organizationIds.length === 0) {
            setSelectedOrgIds([]);
            setSelectedOrgRights({});
        }
        else {
            const updatedOrgRights = {};
            organizationIds.forEach((orgId) => {
                var _a;
                updatedOrgRights[orgId] = (_a = selectedOrgRights[orgId]) !== null && _a !== void 0 ? _a : 'read';
            });
            setSelectedOrgIds(organizationIds);
            setSelectedOrgRights(updatedOrgRights);
        }
    };
    const isOrganizationConfigurableWithRights = (organizationId) => {
        return selectedOrgIds.includes(organizationId);
    };
    const buildPermissionsResult = () => {
        const userOwnerPermission = { type: 'user', externalId: data.createdById, right: 'write' };
        switch (activePermissionType) {
            case 'private': {
                return [userOwnerPermission];
            }
            case 'anyone': {
                return [userOwnerPermission, { type: 'anyone', right: 'read' }];
            }
            case 'organization': {
                const orgPermissions = selectedOrgIds.map((orgId) => {
                    return { type: 'owner', externalId: `org_${orgId}`, right: selectedOrgRights[orgId] };
                });
                return isUserTemplateOwnerOrAdmin ? [userOwnerPermission, ...orgPermissions] : orgPermissions;
            }
            default:
                throw new Error(`Invalid permission type: ${activePermissionType}`);
        }
    };
    return (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', p: 2, gap: 2 } },
        React.createElement(Typography, { id: props.ariaLabelledBy, variant: 'h6', sx: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            } }, t('shareTemplateDialog.title', { templateName: data.title })),
        React.createElement(Typography, { variant: 'body1' }, t('shareTemplateDialog.description')),
        React.createElement(RadioGroup, { value: activePermissionType, onChange: (event) => setActivePermissionType(event.target.value) },
            React.createElement(RadioItem, { label: t('shareTemplateDialog.private'), value: 'private', activeValue: activePermissionType, description: t('shareTemplateDialog.privateDescription'), disabled: !isUserTemplateOwnerOrAdmin, additionalInformation: data.createdBy }),
            React.createElement(RadioItem, { label: t('shareTemplateDialog.public'), value: 'anyone', activeValue: activePermissionType, description: t('shareTemplateDialog.publicDescription'), disabled: !isUserTemplateOwnerOrAdmin }),
            React.createElement(RadioItem, { label: t('shareTemplateDialog.organization'), value: 'organization', activeValue: activePermissionType, description: t('shareTemplateDialog.organizationDescription'), disabled: !isUserTemplateOwnerOrAdmin })),
        activePermissionType === 'organization' && React.createElement(Divider, null),
        activePermissionType === 'organization' && (React.createElement(OrganizationsSelection, { showSelectAllChildren: true, onChangeOrganizationIds: handleOrganizationsChange, organizationIds: selectedOrgIds, staticDialogFilters: {
                orgPermissions: requireAnySystemPermission('all-organizations-admin')
                    ? undefined
                    : ['view-and-message-members', 'create-notifications'],
            }, renderOrganization: (organization) => {
                const orgId = organization.id.split('_')[0];
                return (React.createElement(React.Fragment, null,
                    React.createElement(Typography, { sx: { flexGrow: 1 }, "aria-disabled": organization.disabled }, organization.name),
                    isOrganizationConfigurableWithRights(orgId) && (React.createElement(Box, { sx: { width: 200, ml: 1 } },
                        React.createElement(FormControl, { sx: { width: 200 }, variant: 'outlined', fullWidth: true, margin: 'dense', size: 'small', required: true },
                            React.createElement(Select, { value: selectedOrgRights[orgId], onChange: (event) => handleOrganizationRightChange(orgId, event.target.value) },
                                React.createElement(MenuItem, { key: 'read', value: 'read' }, t('common.read')),
                                React.createElement(MenuItem, { key: 'write', value: 'write' }, t('common.write'))))))));
            } })),
        React.createElement(Box, { sx: { display: 'flex', justifyContent: 'flex-end' } },
            React.createElement(Button, { onClick: () => onClose(), "data-test": 'cancel-dialog-button' }, t('common.cancel')),
            React.createElement(Button, { onClick: () => {
                    data.permissions = buildPermissionsResult();
                    const alertFormData = mapToAlertWizardFormData(data, alertConfig, 'template');
                    onSubmit(alertFormData);
                    onClose();
                }, "data-test": 'share-permissions-button' }, t('common.share')))));
};
const RadioItem = (props) => {
    return (React.createElement(Box, { sx: {
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 1,
            p: 1,
            backgroundColor: themeColors.surface.secondary,
        } },
        React.createElement(Radio, { color: 'primary', value: props.value, checked: props.value === props.activeValue, disabled: props.disabled, inputProps: { 'aria-label': props.label } }),
        React.createElement(Box, { sx: {
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                width: '100%',
            } },
            React.createElement(Box, { sx: {
                    display: 'flex',
                    flexBasis: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    gap: 0.5,
                } },
                React.createElement(Typography, { variant: 'subtitle2' }, props.label),
                props.additionalInformation && (React.createElement(Tooltip, { title: props.additionalInformation, sx: { textOverflow: 'ellipsis', overflow: 'hidden' } },
                    React.createElement(Typography, { variant: 'subtitle2' },
                        "(",
                        props.additionalInformation,
                        ")")))),
            React.createElement(Typography, { variant: 'body2', sx: { flexBasis: '100%', color: themeColors.text.subtle } }, props.description))));
};
